// FinanceExpenses — categorised expense ledger.
//
// Layout:
//   • KPI strip — total, count, biggest category
//   • Category chip row — click one to filter
//   • Ledger table with amount / date / vendor / notes
//   • Add / Edit modal with a category datalist seeded from the
//     workbook's actual expense buckets

import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import {
  Plus, X, Pencil, Trash2, Receipt, DollarSign, TrendingDown
} from "lucide-react";
import { Panel } from "../../../components/primitives";
import { useToast } from "../../../components/Toast";
import { api } from "../../../api";
import {
  fmtMoney, fmtDate, todayIso,
  EXPENSE_CATEGORIES, PAYMENT_MODES, CATEGORY_TONES
} from "./shared";

const EMPTY = {
  expenseDate: "",
  category:    "",
  vendor:      "",
  amount:      "",
  paymentType: "",
  notes:       ""
};

const TONE_PALETTE = ["violet", "gold", "cyan", "green", "orange", "red"];

export default function FinanceExpenses({ seasonId, canWrite }) {
  const toast = useToast();
  const [expenses,  setExpenses]  = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [busy,      setBusy]      = useState(false);
  const [filter,    setFilter]    = useState(null); // active category filter
  const [showForm,  setShowForm]  = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form,      setForm]      = useState(() => ({ ...EMPTY, expenseDate: todayIso() }));

  async function refresh() {
    if (!seasonId) return;
    setLoading(true);
    try {
      const list = await api.listExpenses(seasonId);
      setExpenses(list);
    } catch (err) {
      toast.error(err.message || "Couldn't load expenses.");
    } finally { setLoading(false); }
  }
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [seasonId]);

  useEffect(() => {
    if (!showForm) return;
    const onKey = e => { if (e.key === "Escape") close(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [showForm]);

  const byCategory = useMemo(() => {
    const map = new Map();
    for (const e of expenses) {
      const b = map.get(e.category) || { category: e.category, total: 0, count: 0 };
      b.total += Number(e.amount || 0);
      b.count += 1;
      map.set(e.category, b);
    }
    // Deterministic tone by hash so the pill colors stay stable across renders.
    return [...map.values()]
      .sort((a, b) => b.total - a.total)
      .map((row, i) => ({ ...row, tone: TONE_PALETTE[i % TONE_PALETTE.length] }));
  }, [expenses]);

  const total = byCategory.reduce((s, r) => s + r.total, 0);
  const biggest = byCategory[0] || null;
  const rows = filter ? expenses.filter(e => e.category === filter) : expenses;

  function openCreate() {
    setEditingId(null);
    setForm({ ...EMPTY, expenseDate: todayIso(), category: filter || "" });
    setShowForm(true);
  }
  function openEdit(e) {
    setEditingId(e.id);
    setForm({
      expenseDate: e.expenseDate || todayIso(),
      category:    e.category || "",
      vendor:      e.vendor || "",
      amount:      String(e.amount ?? ""),
      paymentType: e.paymentType || "",
      notes:       e.notes || ""
    });
    setShowForm(true);
  }
  function close() { setShowForm(false); setEditingId(null); }

  async function submit(e) {
    e.preventDefault();
    if (!form.expenseDate) { toast.error("Expense date required."); return; }
    if (!form.category.trim()) { toast.error("Category required."); return; }
    if (!form.amount) { toast.error("Amount required."); return; }
    setBusy(true);
    try {
      const payload = {
        seasonId,
        expenseDate: form.expenseDate,
        category:    form.category.trim(),
        vendor:      form.vendor.trim() || null,
        amount:      Number(form.amount),
        paymentType: form.paymentType || null,
        notes:       form.notes || null
      };
      if (editingId) {
        await api.updateExpense(editingId, payload);
        toast.success("Expense updated.");
      } else {
        await api.createExpense(payload);
        toast.success("Expense recorded.");
      }
      close();
      await refresh();
    } catch (err) {
      toast.error(err.message || "Save failed.");
    } finally { setBusy(false); }
  }

  async function remove(e) {
    if (!confirm(`Delete ${e.category} expense (${fmtMoney(e.amount)})?`)) return;
    try {
      await api.deleteExpense(e.id);
      toast.success("Deleted.");
      await refresh();
    } catch (err) { toast.error(err.message || "Delete failed."); }
  }

  return (
    <div className="stack" style={{gap:16}}>
      <div className="fin-kpis">
        <div className="fin-kpi fin-kpi--red">
          <span className="fin-kpi-icon"><TrendingDown size={18}/></span>
          <span className="fin-kpi-label">Total Expenses</span>
          <b className="fin-kpi-value">{fmtMoney(total)}</b>
        </div>
        <div className="fin-kpi fin-kpi--gold">
          <span className="fin-kpi-icon"><Receipt size={18}/></span>
          <span className="fin-kpi-label">Entries</span>
          <b className="fin-kpi-value">{expenses.length}</b>
        </div>
        <div className="fin-kpi fin-kpi--violet">
          <span className="fin-kpi-icon"><DollarSign size={18}/></span>
          <span className="fin-kpi-label">Biggest Category</span>
          <b className="fin-kpi-value" style={{fontSize:16}}>
            {biggest ? `${biggest.category} · ${fmtMoney(biggest.total)}` : "—"}
          </b>
        </div>
      </div>

      {byCategory.length > 0 && (
        <div className="fin-chipRow">
          <button
            type="button"
            className={`fin-chip${!filter ? " fin-chip--active" : ""}`}
            onClick={() => setFilter(null)}
          >
            All · {fmtMoney(total)}
          </button>
          {byCategory.map(c => (
            <button
              key={c.category}
              type="button"
              className={`fin-chip fin-chip--${c.tone}${filter === c.category ? " fin-chip--active" : ""}`}
              onClick={() => setFilter(c.category)}
              style={{ "--chip-color": CATEGORY_TONES[c.tone] }}
            >
              <span className="fin-chip-dot" style={{background: CATEGORY_TONES[c.tone]}}/>
              {c.category} · {fmtMoney(c.total)}
              <em>{c.count}</em>
            </button>
          ))}
        </div>
      )}

      <Panel title={
        <div style={{display:"flex", alignItems:"center", gap:10, flex:1, flexWrap:"wrap"}}>
          <Receipt size={18}/>
          <span>{filter ? `${filter} · Ledger` : "Expense Ledger"}</span>
          {canWrite && (
            <button type="button" className="btn" onClick={openCreate} style={{marginLeft:"auto"}}>
              <Plus size={13}/> Record Expense
            </button>
          )}
        </div>
      }>
        {loading && <p style={{color:"var(--muted)"}}>Loading…</p>}
        {!loading && rows.length === 0 && (
          <div className="league-annEmpty">
            <Receipt size={28}/>
            <p>
              {filter
                ? `No entries under "${filter}" yet.`
                : "No expenses recorded for this season yet."}
            </p>
          </div>
        )}
        {rows.length > 0 && (
          <div className="fin-tableWrap">
            <table className="fin-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Category</th>
                  <th>Vendor</th>
                  <th style={{textAlign:"right"}}>Amount</th>
                  <th>Payment Type</th>
                  <th>Notes</th>
                  {canWrite && <th className="fin-table-actions">Actions</th>}
                </tr>
              </thead>
              <tbody>
                {rows.map(e => (
                  <tr key={e.id}>
                    <td>{fmtDate(e.expenseDate)}</td>
                    <td><b>{e.category}</b></td>
                    <td>{e.vendor || "—"}</td>
                    <td className="fin-table-money">{fmtMoney(e.amount)}</td>
                    <td>{e.paymentType ? <span className="fin-pill">{e.paymentType}</span> : "—"}</td>
                    <td className="fin-table-notes">{e.notes || ""}</td>
                    {canWrite && (
                      <td className="fin-table-actions">
                        <button type="button" className="league-annCard-iconBtn"
                          onClick={() => openEdit(e)} title="Edit">
                          <Pencil size={12}/>
                        </button>
                        <button type="button" className="league-annCard-iconBtn league-annCard-iconBtn--danger"
                          onClick={() => remove(e)} title="Delete">
                          <Trash2 size={12}/>
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
                <tr className="fin-table-total">
                  <td colSpan={3}><b>Total{filter ? ` · ${filter}` : ""}</b></td>
                  <td className="fin-table-money">
                    <b>{fmtMoney(rows.reduce((s, r) => s + Number(r.amount || 0), 0))}</b>
                  </td>
                  <td colSpan={canWrite ? 3 : 2}></td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      {showForm && createPortal((
        <div className="modal" onClick={close}>
          <div className="modalInner" onClick={e => e.stopPropagation()}>
            <button type="button" className="close" onClick={close} aria-label="Close">
              <X size={20}/>
            </button>
            <Panel title={editingId ? "Edit Expense" : "Record Expense"}>
              <form className="form" onSubmit={submit}>
                <div className="two">
                  <div>
                    <label>Expense Date *</label>
                    <input type="date" required
                      value={form.expenseDate}
                      onChange={e => setForm({ ...form, expenseDate: e.target.value })}/>
                  </div>
                  <div>
                    <label>Amount *</label>
                    <input type="number" step="0.01" required
                      value={form.amount}
                      onChange={e => setForm({ ...form, amount: e.target.value })}
                      placeholder="0.00"/>
                  </div>
                </div>

                <label>Category *</label>
                <input list="fin-expense-categories" required
                  value={form.category}
                  onChange={e => setForm({ ...form, category: e.target.value })}
                  placeholder="Rental / Match Balls / Trophies…"/>
                <datalist id="fin-expense-categories">
                  {EXPENSE_CATEGORIES.map(c => <option key={c} value={c}/>)}
                </datalist>

                <label>Vendor / Payee</label>
                <input value={form.vendor}
                  onChange={e => setForm({ ...form, vendor: e.target.value })}
                  placeholder="Amazon / Sports Cabinet / Linbrook Sports Complex…"/>

                <label>Payment Type</label>
                <input list="fin-payment-modes-exp"
                  value={form.paymentType}
                  onChange={e => setForm({ ...form, paymentType: e.target.value })}
                  placeholder="Zelle / Check / Card…"/>
                <datalist id="fin-payment-modes-exp">
                  {PAYMENT_MODES.map(m => <option key={m} value={m}/>)}
                </datalist>

                <label>Notes</label>
                <textarea rows={2}
                  value={form.notes}
                  onChange={e => setForm({ ...form, notes: e.target.value })}
                  placeholder="Recurring charge, receipt number, etc."/>

                <div style={{display:"flex", gap:10, justifyContent:"flex-end", marginTop:6}}>
                  <button type="button" className="dark" onClick={close} disabled={busy}>Cancel</button>
                  <button type="submit" className="btn" disabled={busy}>
                    {busy ? "Saving…" : editingId ? "Save Changes" : "Record Expense"}
                  </button>
                </div>
              </form>
            </Panel>
          </div>
        </div>
      ), document.body)}
    </div>
  );
}
